import {
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import * as React from 'react';
import { ExternalOrderService } from 'services';
import { SnackbarContext } from 'components';

/**
 * The storefront promo (2026-09-06). One percentage offer, shown as a banner
 * on the ordering page and claimable by anyone: no codes to hand out, no
 * codes to leak.
 *
 * The server re-checks the offer when the order is placed, so a customer who
 * leaves the page open overnight cannot claim a promo that has ended.
 */
const useStyles = makeStyles((theme) => ({
  intro: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  form: { padding: theme.spacing(2), marginBottom: theme.spacing(3) },
  live: {
    background: '#1b5e20',
    color: 'white',
    fontWeight: 700,
  },
  off: { background: '#757575', color: 'white' },
  row: { marginBottom: theme.spacing(2) },
}));

interface Offer {
  id: number;
  title: string;
  description: string | null;
  percent: number;
  minOrder: number;
  startsAt: string | null;
  endsAt: string | null;
  isActive: boolean;
}

const blank = {
  id: 0,
  title: '',
  description: '',
  percent: 10,
  minOrder: 0,
  startsAt: '',
  endsAt: '',
  isActive: false,
};

export const DiscountsTab: React.FC = () => {
  const classes = useStyles();
  const snackbar = React.useContext(SnackbarContext);
  const [offers, setOffers] = React.useState<Offer[]>([]);
  const [live, setLive] = React.useState<any>(null);
  const [form, setForm] = React.useState({ ...blank });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    ExternalOrderService.websiteGetDiscounts()
      .then((r: any) => {
        setOffers(r?.discounts || []);
        setLive(r?.live || null);
      })
      .catch((e: any) => snackbar.error(e))
      .finally(() => setLoading(false));
  }, [snackbar]);

  React.useEffect(() => {
    load();
  }, [load]);

  const save = (overrides?: Partial<typeof blank>) => {
    const payload = { ...form, ...(overrides || {}) };
    if (!payload.title.trim()) {
      snackbar.error(new Error('Give the offer a title customers will understand.'));
      return;
    }
    setSaving(true);
    ExternalOrderService.websiteSaveDiscount({
      id: payload.id || undefined,
      title: payload.title.trim(),
      description: payload.description.trim(),
      percent: Number(payload.percent),
      minOrder: Number(payload.minOrder),
      startsAt: payload.startsAt,
      endsAt: payload.endsAt,
      isActive: payload.isActive,
    })
      .then(() => {
        snackbar.open(payload.isActive ? 'Offer saved and switched ON' : 'Offer saved');
        setForm({ ...blank });
        load();
      })
      .catch((e: any) => snackbar.error(e))
      .finally(() => setSaving(false));
  };

  const toggle = (offer: Offer, on: boolean) => {
    setSaving(true);
    ExternalOrderService.websiteSaveDiscount({
      id: offer.id,
      title: offer.title,
      description: offer.description || '',
      percent: offer.percent,
      minOrder: offer.minOrder,
      startsAt: offer.startsAt || '',
      endsAt: offer.endsAt || '',
      isActive: on,
    })
      .then(() => {
        snackbar.open(on ? 'Offer is now LIVE on the ordering page' : 'Offer switched off');
        load();
      })
      .catch((e: any) => snackbar.error(e))
      .finally(() => setSaving(false));
  };

  const edit = (offer: Offer) =>
    setForm({
      id: offer.id,
      title: offer.title,
      description: offer.description || '',
      percent: offer.percent,
      minOrder: offer.minOrder,
      startsAt: offer.startsAt || '',
      endsAt: offer.endsAt || '',
      isActive: offer.isActive,
    });

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <Paper className={classes.intro} elevation={0} variant="outlined">
        <Typography variant="body2">
          A percentage off the whole order, shown as a banner on the ordering
          page. Anybody can claim it, there is no code to type. Only one offer
          runs at a time: switching one on switches the others off.
        </Typography>
        <Typography variant="body2" style={{ marginTop: 8 }}>
          {live ? (
            <>
              Live right now: <strong>{live.title}</strong> — {live.percent}% off
              {live.minOrder > 0 ? ` on orders over €${live.minOrder.toFixed(2)}` : ''}
              {live.endsAt ? `, until ${live.endsAt}` : ''}.
            </>
          ) : (
            'Nothing is running at the moment.'
          )}
        </Typography>
      </Paper>

      <Paper className={classes.form}>
        <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 12 }}>
          {form.id ? `Editing offer #${form.id}` : 'New offer'}
        </Typography>
        <Grid container spacing={2} className={classes.row}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Title (customers see this)"
              placeholder="10% off your first online order"
              fullWidth
              size="small"
              variant="outlined"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Small print (optional)"
              placeholder="Order online and save"
              fullWidth
              size="small"
              variant="outlined"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Percent off"
              type="number"
              fullWidth
              size="small"
              variant="outlined"
              value={form.percent}
              onChange={(e) => setForm({ ...form, percent: Number(e.target.value) })}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Minimum order (€)"
              type="number"
              fullWidth
              size="small"
              variant="outlined"
              helperText="0 = no minimum"
              value={form.minOrder}
              onChange={(e) => setForm({ ...form, minOrder: Number(e.target.value) })}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Starts"
              type="datetime-local"
              fullWidth
              size="small"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              helperText="Blank = as soon as it is on"
              value={form.startsAt.replace(' ', 'T')}
              onChange={(e) => setForm({ ...form, startsAt: e.target.value.replace('T', ' ') })}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Ends"
              type="datetime-local"
              fullWidth
              size="small"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              helperText="Blank = until switched off"
              value={form.endsAt.replace(' ', 'T')}
              onChange={(e) => setForm({ ...form, endsAt: e.target.value.replace('T', ' ') })}
            />
          </Grid>
        </Grid>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="primary"
            disabled={saving}
            onClick={() => save({ isActive: false })}
          >
            Save (off)
          </Button>
          <Button
            variant="contained"
            style={{ background: '#1b5e20', color: '#fff' }}
            disabled={saving}
            onClick={() => save({ isActive: true })}
          >
            Save and switch ON
          </Button>
          {form.id ? (
            <Button disabled={saving} onClick={() => setForm({ ...blank })}>
              Cancel edit
            </Button>
          ) : null}
        </div>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Offer</TableCell>
              <TableCell align="right">Off</TableCell>
              <TableCell align="right">Minimum</TableCell>
              <TableCell>Window</TableCell>
              <TableCell align="center">Live</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {offers.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="textSecondary">
                    No offers yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {offers.map((o) => (
              <TableRow key={o.id} hover>
                <TableCell>
                  <div style={{ fontWeight: 600 }}>{o.title}</div>
                  {o.description ? (
                    <div style={{ fontSize: '0.75rem', color: '#666' }}>{o.description}</div>
                  ) : null}
                </TableCell>
                <TableCell align="right">{o.percent}%</TableCell>
                <TableCell align="right">
                  {o.minOrder > 0 ? `€${o.minOrder.toFixed(2)}` : '—'}
                </TableCell>
                <TableCell>
                  {o.startsAt || 'now'} → {o.endsAt || 'until switched off'}
                </TableCell>
                <TableCell align="center">
                  <Switch
                    checked={o.isActive}
                    disabled={saving}
                    onChange={(e) => toggle(o, e.target.checked)}
                  />
                  <Chip
                    size="small"
                    label={o.isActive ? 'LIVE' : 'off'}
                    className={o.isActive ? classes.live : classes.off}
                  />
                </TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => edit(o)}>
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
